import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser, useAuth } from '@clerk/clerk-react';
import { createPortal } from 'react-dom';
import { getApiUrl } from '../../utils/apiConfig';

const UserManagementContainer = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
    border-radius: 24px 24px 0 0;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const BackButton = styled.button`
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
`;

const Title = styled.h2`
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ActionButton = styled.button`
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const UsersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const UserCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const UserAvatar = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 1rem;
`;

const UserAvatarPlaceholder = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  margin-bottom: 1rem;
`;

const UserName = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #374151;
  font-size: 1.1rem;
  font-weight: 600;
`;

const UserEmail = styled.p`
  margin: 0 0 0.5rem 0;
  color: #6b7280;
  font-size: 0.9rem;
`;

const UserRole = styled.span`
  display: inline-block;
  background: ${props => props.role === 'admin' ? 'rgba(46, 204, 113, 0.1)' : 'rgba(52, 152, 219, 0.1)'};
  color: ${props => props.role === 'admin' ? '#2ecc71' : '#3498db'};
  border: 1px solid ${props => props.role === 'admin' ? 'rgba(46, 204, 113, 0.2)' : 'rgba(52, 152, 219, 0.2)'};
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const UserActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const SmallButton = styled.button`
  background: ${props => props.variant === 'danger' ? 'rgba(231, 76, 60, 0.1)' : 
               props.variant === 'edit' ? 'rgba(52, 152, 219, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  color: ${props => props.variant === 'danger' ? '#e74c3c' : 
           props.variant === 'edit' ? '#3498db' : '#374151'};
  border: 1px solid ${props => props.variant === 'danger' ? 'rgba(231, 76, 60, 0.2)' : 
                      props.variant === 'edit' ? 'rgba(52, 152, 219, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background: ${props => props.variant === 'danger' ? 'rgba(231, 76, 60, 0.2)' : 
                 props.variant === 'edit' ? 'rgba(52, 152, 219, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
    transform: translateY(-1px);
    box-shadow: ${props => props.variant === 'edit' ? '0 4px 12px rgba(52, 152, 219, 0.2)' : 
                 props.variant === 'danger' ? '0 4px 12px rgba(231, 76, 60, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  font-size: 1.1rem;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 8px;
  padding: 1rem;
  color: #e74c3c;
  margin-bottom: 1rem;
`;

const AccessDeniedContainer = styled.div`
  text-align: center;
  padding: 3rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const AccessDeniedIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const AccessDeniedTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #374151;
  font-size: 1.5rem;
`;

const AccessDeniedText = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.5;
`;

const ModalOverlay = styled(motion.div)`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #374151;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #e5e7eb;
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessModal = styled(motion.div)`
  position: fixed !important;
  top: 20px !important;
  right: 20px !important;
  background: linear-gradient(135deg, #2ecc71, #27ae60);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(46, 204, 113, 0.3);
  z-index: 10001;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  font-size: 0.95rem;
  max-width: 350px;
  backdrop-filter: blur(10px);
`;

const SuccessIcon = styled.div`
  font-size: 1.2rem;
  display: flex;
  align-items: center;
`;

const SuccessText = styled.div`
  flex: 1;
`;

const CloseSuccessButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const SuccessNotification = ({ message, onClose }) => {
  if (!message) return null;

  return createPortal(
    <SuccessModal
      initial={{ opacity: 0, x: 100, scale: 0.8 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 100, scale: 0.8 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <SuccessIcon>✅</SuccessIcon>
      <SuccessText>{message}</SuccessText>
      <CloseSuccessButton onClick={onClose}>×</CloseSuccessButton>
    </SuccessModal>,
    document.body
  );
};

const EditUserModal = ({ editingUser, editForm, setEditForm, onClose, onSave }) => {
  if (!editingUser) return null;

  return createPortal(
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          initial={{ opacity: 0, scale: 0.8, y: -50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -50 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>Benutzer bearbeiten</ModalTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </ModalHeader>
          
          <ModalBody>
            <FormGroup>
              <Label>Vorname</Label>
              <Input
                type="text"
                value={editForm.firstName}
                onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                placeholder="Vorname eingeben"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Nachname</Label>
              <Input
                type="text"
                value={editForm.lastName}
                onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                placeholder="Nachname eingeben"
              />
            </FormGroup>
            
            <FormGroup>
              <Label>Rolle</Label>
              <Select
                value={editForm.role}
                onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="user">Benutzer</option>
                <option value="admin">Administrator</option>
              </Select>
            </FormGroup>
          </ModalBody>
          
          <ModalActions>
            <ModalButton className="secondary" onClick={onClose}>
              Abbrechen
            </ModalButton>
            <ModalButton className="primary" onClick={onSave}>
              Speichern
            </ModalButton>
          </ModalActions>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>,
    document.body
  );
};

const UserManagement = ({ onBack }) => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    role: 'user'
  });
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    checkUserPermission();
  }, [user]);

  const checkUserPermission = async () => {
    try {
      setLoading(true);
      setError(null);

      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      const response = await fetch(getApiUrl('/admin/check-user-permission'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        setHasPermission(data.hasPermission || false);
        if (data.hasPermission) {
          loadUsers();
        }
      } else {
        setError(data.error || 'Fehler beim Überprüfen der Berechtigung');
        setHasPermission(false);
      }
    } catch (err) {
      console.error('Error checking permission:', err);
      setError('Fehler beim Überprüfen der Berechtigung');
      setHasPermission(false);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      const response = await fetch(getApiUrl('/admin/users'), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        setUsers(data.data || []);
      } else {
        setError(data.error || 'Fehler beim Laden der Benutzer');
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    // Automatisch nach 4 Sekunden verstecken
    setTimeout(() => {
      setSuccessMessage(null);
    }, 4000);
  };

  const handleCloseSuccessMessage = () => {
    setSuccessMessage(null);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.publicMetadata?.role || 'user'
    });
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
    setEditForm({
      firstName: '',
      lastName: '',
      role: 'user'
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      const response = await fetch(getApiUrl(`/admin/users/${editingUser.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          publicMetadata: {
            role: editForm.role
          }
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        // Aktualisiere die Benutzerliste
        setUsers(prev => prev.map(u => 
          u.id === editingUser.id 
            ? {
                ...u,
                firstName: editForm.firstName,
                lastName: editForm.lastName,
                publicMetadata: { role: editForm.role }
              }
            : u
        ));
        handleCloseEditModal();
        showSuccessMessage('Benutzer erfolgreich aktualisiert!');
      } else {
        alert(data.error || 'Fehler beim Aktualisieren des Benutzers');
      }
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Fehler beim Aktualisieren des Benutzers');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten?')) {
      return;
    }

    try {
      let token;
      try {
        token = await getToken();
      } catch (err) {
        token = 'dev-clerk-token';
      }

      const response = await fetch(getApiUrl(`/admin/users/${userId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (response.ok) {
        setUsers(prev => prev.filter(user => user.id !== userId));
        showSuccessMessage('Benutzer erfolgreich gelöscht!');
      } else {
        alert(data.error || 'Fehler beim Löschen des Benutzers');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      alert('Fehler beim Löschen des Benutzers');
    }
  };

  const getUserInitials = (firstName, lastName) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return 'U';
  };

  if (loading) {
    return (
      <UserManagementContainer>
        <Header>
          <HeaderLeft>
            <BackButton onClick={onBack}>
              ← Zurück zum Dashboard
            </BackButton>
            <Title>Benutzerverwaltung</Title>
          </HeaderLeft>
        </Header>
        <LoadingSpinner>Berechtigung wird überprüft...</LoadingSpinner>
      </UserManagementContainer>
    );
  }

  if (!hasPermission) {
    return (
      <UserManagementContainer>
        <Header>
          <HeaderLeft>
            <BackButton onClick={onBack}>
              ← Zurück zum Dashboard
            </BackButton>
            <Title>Benutzerverwaltung</Title>
          </HeaderLeft>
        </Header>

        <AccessDeniedContainer>
          <AccessDeniedIcon>🔒</AccessDeniedIcon>
          <AccessDeniedTitle>Zugriff nicht erlaubt</AccessDeniedTitle>
          <AccessDeniedText>
            Sie haben keine Berechtigung zur Benutzerverwaltung.
            <br />
            Kontaktieren Sie einen Administrator, um die Berechtigung <strong>clerkUserEdit</strong> zu erhalten.
          </AccessDeniedText>
        </AccessDeniedContainer>
      </UserManagementContainer>
    );
  }

  return (
    <UserManagementContainer>
      <Header>
        <HeaderLeft>
          <BackButton onClick={onBack}>
            ← Zurück zum Dashboard
          </BackButton>
          <Title>Benutzerverwaltung</Title>
        </HeaderLeft>
        <ActionButton onClick={loadUsers}>
          🔄 Aktualisieren
        </ActionButton>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading ? (
        <LoadingSpinner>Benutzer werden geladen...</LoadingSpinner>
      ) : (
        <UsersGrid>
          <AnimatePresence>
            {users.map((user, index) => (
              <UserCard
                key={user.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                {user.imageUrl ? (
                  <UserAvatar src={user.imageUrl} alt={`${user.firstName} ${user.lastName}`} />
                ) : (
                  <UserAvatarPlaceholder>
                    {getUserInitials(user.firstName, user.lastName)}
                  </UserAvatarPlaceholder>
                )}
                
                <UserName>
                  {user.firstName} {user.lastName}
                </UserName>
                
                <UserEmail>
                  {user.emailAddresses?.[0]?.emailAddress || 'Keine E-Mail'}
                </UserEmail>
                
                <UserRole role={user.publicMetadata?.role || 'user'}>
                  {user.publicMetadata?.role || 'user'}
                </UserRole>
                
                <UserActions>
                  <SmallButton variant="edit" onClick={() => handleEditUser(user)}>
                    ✏️ Bearbeiten
                  </SmallButton>
                  {user.id !== user?.id && (
                    <SmallButton 
                      variant="danger"
                      onClick={() => handleDeleteUser(user.id)}
                    >
                      🗑️ Löschen
                    </SmallButton>
                  )}
                </UserActions>
              </UserCard>
            ))}
          </AnimatePresence>
        </UsersGrid>
      )}

      {/* Bearbeitungsmodal - wird außerhalb des Containers gerendert */}
      <EditUserModal
        editingUser={editingUser}
        editForm={editForm}
        setEditForm={setEditForm}
        onClose={handleCloseEditModal}
        onSave={handleSaveUser}
      />

      {/* Success-Notification - wird außerhalb des Containers gerendert */}
      <SuccessNotification
        message={successMessage}
        onClose={handleCloseSuccessMessage}
      />
    </UserManagementContainer>
  );
};

export default UserManagement;
